import {
  PROTOCOL_VERSION,
  RECONNECT_WINDOW_MS,
  STAY_WINDOW_MS,
  sanitizeInterests,
  type IceServerConfig,
  type PeerProfile,
  type ServerMessage,
  type SkipReason,
  type Stats,
} from '@phidio/protocol';
import { Signaling, type SignalingStatus } from './signaling.ts';
import {
  isMobile,
  listCameras,
  requestMedia,
  stopStream,
  toMediaError,
  type FacingMode,
} from './media.ts';
import { haptic, play, setSoundEnabled, unlockAudio } from './sound.ts';
import {
  bumpStreak,
  loadProfile,
  loadSparks,
  loadStats,
  newlyEarnedBadges,
  rememberSpark,
  saveProfile,
  saveSparks,
  saveStats,
  type Badge,
  type PlayerStats,
  type Profile,
  type SavedSpark,
} from './storage.ts';

export type Phase =
  | 'idle'
  | 'preparing'
  | 'searching'
  | 'connecting'
  | 'live'
  | 'ended'
  | 'error';

export interface ChatEntry {
  id: string;
  from: 'me' | 'them' | 'system';
  text: string;
  at: number;
}

export interface FloatingReaction {
  id: string;
  emoji: string;
  from: 'me' | 'them';
  x: number;
}

export type Quality = 'unknown' | 'great' | 'ok' | 'poor';

/** An in-flight reunion attempt, in either direction. */
export interface ReconnectState {
  token: string;
  name: string;
  direction: 'outgoing' | 'incoming';
  /** When the request was raised, so the UI can run a countdown. */
  at: number;
}

export interface EngineState {
  phase: Phase;
  connection: SignalingStatus;

  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  remoteHasVideo: boolean;

  peer: PeerProfile | null;
  common: string[];

  messages: ChatEntry[];
  unread: number;
  peerTyping: boolean;
  reactions: FloatingReaction[];

  micOn: boolean;
  camOn: boolean;
  facing: FacingMode;
  canFlipCamera: boolean;

  liked: boolean;
  likedByPeer: boolean;
  spark: boolean;

  /** Shared conversation starter for the current room. */
  prompt: string | null;
  promptOpen: boolean;

  /** Mutual-stay handshake. */
  stayAsked: boolean;
  stayInvited: boolean;
  stayed: boolean;
  /** Next needs a second tap once the conversation is committed. */
  confirmNext: boolean;

  /** Past mutual likes, and which of them are online right now. */
  sparks: SavedSpark[];
  sparksOnline: string[];
  reconnect: ReconnectState | null;

  serverStats: Stats;
  player: PlayerStats;
  profile: Profile;

  callStartedAt: number | null;
  searchStartedAt: number | null;
  queuePosition: number;

  quality: Quality;
  error: string | null;
  notice: string | null;
  badgeToasts: Badge[];
}

const EMPTY_STATS: Stats = { online: 0, waiting: 0, inCall: 0, connectionsMade: 0 };

type DataMessage =
  | { k: 'chat'; text: string }
  | { k: 'reaction'; emoji: string }
  | { k: 'typing'; on: boolean };

const uid = (): string =>
  crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Owns every piece of mutable call state: the socket, the peer connection, the
 * media tracks and the local player progression. React subscribes to immutable
 * snapshots through `useSyncExternalStore`, so none of this lives in effects.
 */
export class PhidioEngine {
  private listeners = new Set<() => void>();
  private snapshot: EngineState;

  private readonly signaling: Signaling;
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private iceServers: IceServerConfig[] = [{ urls: 'stun:stun.l.google.com:19302' }];

  private polite = false;
  private makingOffer = false;
  private ignoreOffer = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];

  private typingTimer: number | null = null;
  private statsTimer: number | null = null;
  private reactionTimers = new Set<number>();
  private searchNudgeTimer: number | null = null;
  private stayTimer: number | null = null;
  private confirmTimer: number | null = null;
  private reconnectTimer: number | null = null;
  /** True when a reunion was started from the landing page, so failure returns there. */
  private reconnectFromIdle = false;

  constructor() {
    const profile = loadProfile();
    setSoundEnabled(profile.soundOn);

    this.snapshot = {
      phase: 'idle',
      connection: 'idle',
      localStream: null,
      remoteStream: null,
      remoteHasVideo: false,
      peer: null,
      common: [],
      messages: [],
      unread: 0,
      peerTyping: false,
      reactions: [],
      micOn: true,
      camOn: true,
      facing: 'user',
      canFlipCamera: false,
      liked: false,
      likedByPeer: false,
      spark: false,
      prompt: null,
      promptOpen: false,
      stayAsked: false,
      stayInvited: false,
      stayed: false,
      confirmNext: false,
      sparks: loadSparks(),
      sparksOnline: [],
      reconnect: null,
      serverStats: EMPTY_STATS,
      player: loadStats(),
      profile,
      callStartedAt: null,
      searchStartedAt: null,
      queuePosition: 0,
      quality: 'unknown',
      error: null,
      notice: null,
      badgeToasts: [],
    };

    this.signaling = new Signaling({
      onMessage: (message) => this.onServerMessage(message),
      onStatus: (status) => this.onSignalingStatus(status),
    });

    window.addEventListener('pagehide', () => this.dispose());
  }

  /* ---------------------------------------------------------------- */
  /* Store plumbing                                                    */
  /* ---------------------------------------------------------------- */

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): EngineState => this.snapshot;

  private patch(changes: Partial<EngineState>): void {
    this.snapshot = { ...this.snapshot, ...changes };
    for (const listener of this.listeners) listener();
  }

  /* ---------------------------------------------------------------- */
  /* Public API                                                        */
  /* ---------------------------------------------------------------- */

  updateProfile(changes: Partial<Profile>): void {
    const profile = { ...this.snapshot.profile, ...changes };
    if (changes.interests) profile.interests = sanitizeInterests(changes.interests);
    saveProfile(profile);
    setSoundEnabled(profile.soundOn);
    this.patch({ profile });
  }

  /**
   * Opens the presence socket without touching the camera. Called as soon as the
   * page mounts so the landing page shows live numbers and, more importantly, so
   * a past spark can reach you the moment they come back.
   */
  wake(): void {
    this.signaling.connect();
  }

  /** Entry point from the big red button: grab media, connect, start searching. */
  async start(): Promise<void> {
    unlockAudio();
    if (this.snapshot.phase === 'searching' || this.snapshot.phase === 'live') return;

    this.patch({ phase: 'preparing', error: null, notice: null });

    if (!(await this.ensureMedia())) return;

    this.signaling.connect();
    this.beginSearch();
  }

  /** Grabs the camera if we don't hold it yet. Returns false when denied. */
  private async ensureMedia(): Promise<boolean> {
    if (this.snapshot.localStream) return true;
    try {
      const stream = await requestMedia(this.snapshot.facing);
      this.applyLocalStream(stream);
      void listCameras().then((cameras) =>
        this.patch({ canFlipCamera: cameras.length > 1 || isMobile() }),
      );
      return true;
    } catch (error) {
      const media = toMediaError(error);
      play('error');
      this.patch({ phase: 'error', error: media.message });
      return false;
    }
  }

  /** Ask for a different stranger. */
  next(): void {
    if (this.snapshot.phase === 'idle' || this.snapshot.phase === 'error') return;

    // Once both people committed to the conversation, leaving takes two taps.
    if (this.snapshot.stayed && !this.snapshot.confirmNext) {
      haptic(8);
      this.patch({ confirmNext: true });
      if (this.confirmTimer !== null) clearTimeout(this.confirmTimer);
      this.confirmTimer = window.setTimeout(() => this.patch({ confirmNext: false }), 3200);
      return;
    }

    haptic(18);
    play('skip');
    this.finishCall('skipped');
    this.signaling.send({ t: 'skip' });
    this.beginSearch();
  }

  /** Leave entirely and release the camera. The presence socket stays open. */
  stop(): void {
    this.finishCall('left');
    this.signaling.send({ t: 'hangup' });
    this.teardownPeer();
    stopStream(this.snapshot.localStream);
    this.cancelReconnect();
    this.patch({
      phase: 'idle',
      localStream: null,
      remoteStream: null,
      peer: null,
      common: [],
      messages: [],
      searchStartedAt: null,
      callStartedAt: null,
      notice: null,
    });
  }

  toggleMic(): void {
    const micOn = !this.snapshot.micOn;
    this.snapshot.localStream?.getAudioTracks().forEach((track) => (track.enabled = micOn));
    haptic(8);
    this.patch({ micOn });
  }

  toggleCam(): void {
    const camOn = !this.snapshot.camOn;
    this.snapshot.localStream?.getVideoTracks().forEach((track) => (track.enabled = camOn));
    haptic(8);
    this.patch({ camOn });
  }

  toggleMirror(): void {
    this.updateProfile({ mirror: !this.snapshot.profile.mirror });
  }

  toggleSound(): void {
    const soundOn = !this.snapshot.profile.soundOn;
    this.updateProfile({ soundOn });
    if (soundOn) {
      unlockAudio();
      play('message');
    }
  }

  /** Swaps between front and rear camera without renegotiating the session. */
  async flipCamera(): Promise<void> {
    const facing: FacingMode = this.snapshot.facing === 'user' ? 'environment' : 'user';
    const current = this.snapshot.localStream;
    if (!current) {
      this.patch({ facing });
      return;
    }

    try {
      const fresh = await requestMedia(facing);
      const videoTrack = fresh.getVideoTracks()[0];
      if (!videoTrack) {
        stopStream(fresh);
        return;
      }

      const sender = this.pc?.getSenders().find((s) => s.track?.kind === 'video');
      await sender?.replaceTrack(videoTrack);

      const audioTrack = current.getAudioTracks()[0];
      current.getVideoTracks().forEach((track) => {
        current.removeTrack(track);
        track.stop();
      });
      current.addTrack(videoTrack);
      fresh.getAudioTracks().forEach((track) => track.stop());
      if (audioTrack) audioTrack.enabled = this.snapshot.micOn;
      videoTrack.enabled = this.snapshot.camOn;

      this.patch({ facing, localStream: current });
    } catch (error) {
      this.patch({ notice: toMediaError(error).message });
    }
  }

  sendChat(text: string): void {
    const clean = text.replace(/\s+/g, ' ').trim().slice(0, 500);
    if (!clean || !this.snapshot.peer) return;

    if (!this.sendData({ k: 'chat', text: clean })) {
      // Data channel not ready — the server relays instead.
      this.signaling.send({ t: 'chat', text: clean });
    }
    this.pushMessage({ id: uid(), from: 'me', text: clean, at: Date.now() });
    this.setTyping(false);
  }

  setTyping(on: boolean): void {
    this.sendData({ k: 'typing', on });
  }

  react(emoji: string): void {
    if (!this.snapshot.peer) return;
    haptic(6);
    this.sendData({ k: 'reaction', emoji });
    this.spawnReaction(emoji, 'me');
  }

  like(): void {
    if (this.snapshot.liked || !this.snapshot.peer) return;
    haptic([10, 40, 10]);
    this.signaling.send({ t: 'like' });
    this.patch({ liked: true });
  }

  /* ---------------------------------------------------------------- */
  /* Staying, ice-breakers and reunions                                */
  /* ---------------------------------------------------------------- */

  /** Invite the other person to keep talking, or accept their invitation. */
  stay(): void {
    const { peer, stayed, stayInvited } = this.snapshot;
    if (!peer || stayed || stayInvited) return;
    haptic([8, 30, 8]);
    this.signaling.send({ t: 'stay' });
    this.patch({ stayInvited: true, stayAsked: false });
  }

  /** Roll a fresh conversation starter for both of you. */
  newPrompt(): void {
    if (!this.snapshot.peer) return;
    haptic(6);
    this.signaling.send({ t: 'new-prompt' });
  }

  togglePrompt(): void {
    this.patch({ promptOpen: !this.snapshot.promptOpen });
  }

  dismissPrompt(): void {
    if (this.snapshot.promptOpen) this.patch({ promptOpen: false });
  }

  /** Ask a past spark to talk again. Needs the camera, so it may prompt for it. */
  async reconnectTo(token: string): Promise<void> {
    unlockAudio();
    const spark = this.snapshot.sparks.find((item) => item.token === token);
    if (!spark) return;

    this.reconnectFromIdle = this.snapshot.phase === 'idle' || this.snapshot.phase === 'error';
    if (this.reconnectFromIdle) this.patch({ phase: 'preparing', error: null, notice: null });
    if (!(await this.ensureMedia())) return;

    this.signaling.connect();
    this.beginReunion(token, spark.name, 'outgoing');
    this.signaling.send({ t: 'reconnect', token });
  }

  /** Accept an incoming reunion request — the server pairs you immediately. */
  async acceptReconnect(): Promise<void> {
    const pending = this.snapshot.reconnect;
    if (!pending || pending.direction !== 'incoming') return;

    unlockAudio();
    this.reconnectFromIdle = this.snapshot.phase === 'idle' || this.snapshot.phase === 'error';
    if (this.reconnectFromIdle) this.patch({ phase: 'preparing', error: null, notice: null });
    if (!(await this.ensureMedia())) return;

    this.beginReunion(pending.token, pending.name, 'outgoing');
    this.signaling.send({ t: 'reconnect', token: pending.token });
  }

  /** Turn down an incoming request, or withdraw your own. */
  declineReconnect(): void {
    const pending = this.snapshot.reconnect;
    if (!pending) return;
    this.signaling.send(
      pending.direction === 'incoming'
        ? { t: 'reconnect-decline', token: pending.token }
        : { t: 'reconnect-cancel', token: pending.token },
    );
    this.cancelReconnect();
    if (this.snapshot.phase === 'searching' && !this.reconnectFromIdle) this.beginSearch();
    else if (this.reconnectFromIdle) this.patch({ phase: 'idle' });
  }

  /** Remove a spark for good, on both sides. */
  forgetSpark(token: string): void {
    this.signaling.send({ t: 'forget', token });
    const sparks = this.snapshot.sparks.filter((spark) => spark.token !== token);
    saveSparks(sparks);
    this.patch({
      sparks,
      sparksOnline: this.snapshot.sparksOnline.filter((item) => item !== token),
    });
  }

  private beginReunion(token: string, name: string, direction: 'outgoing' | 'incoming'): void {
    this.teardownPeer();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);

    this.patch({
      phase: 'searching',
      reconnect: { token, name, direction, at: Date.now() },
      peer: null,
      common: [],
      remoteStream: null,
      remoteHasVideo: false,
      messages: [],
      unread: 0,
      liked: false,
      likedByPeer: false,
      callStartedAt: null,
      searchStartedAt: Date.now(),
      quality: 'unknown',
      queuePosition: 0,
    });

    this.reconnectTimer = window.setTimeout(
      () => this.failReconnect('timeout'),
      RECONNECT_WINDOW_MS + 5_000,
    );
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.snapshot.reconnect) this.patch({ reconnect: null });
  }

  private failReconnect(reason: 'offline' | 'expired' | 'declined' | 'cancelled' | 'timeout'): void {
    const pending = this.snapshot.reconnect;
    if (!pending) return;

    const name = pending.name;
    const notices: Record<typeof reason, string> = {
      offline: `${name} isn't online right now — we'll tell you when they are.`,
      expired: 'That spark has expired.',
      declined: `${name} can't talk right now.`,
      cancelled: `${name} withdrew the request.`,
      timeout: `${name} didn't answer.`,
    };

    const incoming = pending.direction === 'incoming';
    this.cancelReconnect();
    if (incoming) return;

    this.patch({ notice: notices[reason] });
    if (this.reconnectFromIdle) {
      this.reconnectFromIdle = false;
      this.stop();
    } else if (this.snapshot.phase === 'searching') {
      this.beginSearch();
    }
  }

  report(reason: 'nudity' | 'harassment' | 'minor' | 'spam' | 'other'): void {
    if (!this.snapshot.peer) return;
    this.signaling.send({ t: 'report', reason });
    this.finishCall('reported');
    this.patch({ notice: 'Thanks — that person was reported. Finding someone new…' });
    this.beginSearch();
  }

  markChatRead(): void {
    if (this.snapshot.unread) this.patch({ unread: 0 });
  }

  dismissBadge(id: string): void {
    this.patch({ badgeToasts: this.snapshot.badgeToasts.filter((badge) => badge.id !== id) });
  }

  dismissNotice(): void {
    this.patch({ notice: null });
  }

  dismissSpark(): void {
    this.patch({ spark: false });
  }

  dispose(): void {
    this.teardownPeer();
    stopStream(this.snapshot.localStream);
    this.signaling.close();
  }

  private applyLocalStream(stream: MediaStream): void {
    stream.getAudioTracks().forEach((track) => (track.enabled = this.snapshot.micOn));
    stream.getVideoTracks().forEach((track) => (track.enabled = this.snapshot.camOn));
    this.patch({ localStream: stream });
  }

  /* ---------------------------------------------------------------- */
  /* Search + signaling                                                */
  /* ---------------------------------------------------------------- */

  private beginSearch(): void {
    this.teardownPeer();
    this.cancelReconnect();
    this.reconnectFromIdle = false;
    play('search');

    this.patch({
      phase: 'searching',
      peer: null,
      common: [],
      remoteStream: null,
      remoteHasVideo: false,
      messages: [],
      unread: 0,
      peerTyping: false,
      liked: false,
      likedByPeer: false,
      prompt: null,
      promptOpen: false,
      stayAsked: false,
      stayInvited: false,
      stayed: false,
      confirmNext: false,
      callStartedAt: null,
      searchStartedAt: Date.now(),
      quality: 'unknown',
      queuePosition: 0,
    });

    this.sendQueue();

    if (this.searchNudgeTimer !== null) clearTimeout(this.searchNudgeTimer);
    this.searchNudgeTimer = window.setTimeout(() => {
      if (this.snapshot.phase === 'searching' && this.snapshot.profile.strict) {
        this.patch({
          notice: 'Not many people share your interests right now — widening the search.',
        });
      }
    }, 15_000);
  }

  private sendQueue(): void {
    const { profile } = this.snapshot;
    this.signaling.send({
      t: 'queue',
      interests: profile.interests,
      strict: profile.strict && profile.interests.length > 0,
    });
  }

  private sayHello(): void {
    const { profile, sparks } = this.snapshot;
    this.signaling.send({
      t: 'hello',
      v: PROTOCOL_VERSION,
      name: profile.name.trim() || 'Stranger',
      interests: profile.interests,
      lang: navigator.language?.split('-')[0] ?? null,
      tokens: sparks.map((spark) => spark.token),
    });
  }

  private onSignalingStatus(connection: SignalingStatus): void {
    this.patch({ connection });
    if (connection === 'open') {
      this.sayHello();
      if (this.snapshot.phase === 'searching') this.sendQueue();
    }
    if (connection === 'reconnecting' && this.snapshot.phase === 'live') {
      this.patch({ notice: 'Connection lost — reconnecting…' });
    }
  }

  private onServerMessage(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        this.iceServers = message.iceServers;
        this.patch({ serverStats: message.stats });
        break;

      case 'stats':
        this.patch({ serverStats: message.stats });
        break;

      case 'queued':
        this.patch({ queuePosition: message.position });
        break;

      case 'matched':
        void this.onMatched(message);
        break;

      case 'signal':
        void this.onRemoteSignal(message);
        break;

      case 'peer-left':
        this.onPeerLeft(message.reason);
        break;

      case 'chat':
        this.receiveChat(message.text);
        break;

      case 'liked':
        this.patch({ likedByPeer: true });
        haptic(10);
        break;

      case 'spark':
        this.onSpark(message.token, message.peer);
        break;

      case 'prompt':
        play('message');
        this.patch({ prompt: message.text, promptOpen: true });
        break;

      case 'stay-request':
        this.onStayRequest();
        break;

      case 'stayed':
        this.onStayed();
        break;

      case 'sparks':
        this.patch({ sparksOnline: message.online });
        break;

      case 'spark-presence':
        this.onSparkPresence(message.token, message.online, message.name);
        break;

      case 'reconnect-request':
        this.onReconnectRequest(message.token, message.name);
        break;

      case 'reconnect-pending':
        if (this.snapshot.reconnect?.token === message.token) {
          this.patch({ reconnect: { ...this.snapshot.reconnect, name: message.name } });
        }
        break;

      case 'reconnect-failed':
        if (this.snapshot.reconnect?.token === message.token) this.failReconnect(message.reason);
        break;

      case 'error':
        play('error');
        this.patch(
          message.code === 'banned' || message.code === 'version-mismatch'
            ? { phase: 'error', error: message.message }
            : { notice: message.message },
        );
        break;

      default:
        break;
    }
  }

  /* ---------------------------------------------------------------- */
  /* Sparks, stay, reunions                                            */
  /* ---------------------------------------------------------------- */

  private onSpark(token: string, peer: PeerProfile): void {
    play('spark');
    haptic([15, 60, 15, 60, 25]);
    this.awardStats({ sparks: 1 });

    const sparks = rememberSpark(this.snapshot.sparks, {
      token,
      name: peer.name,
      country: peer.country,
      at: Date.now(),
    });
    saveSparks(sparks);

    this.patch({
      spark: true,
      liked: true,
      likedByPeer: true,
      sparks,
      // The other side is by definition online right now.
      sparksOnline: [...new Set([...this.snapshot.sparksOnline, token])],
    });
  }

  private onStayRequest(): void {
    if (this.snapshot.stayed) return;
    play('knock');
    haptic([10, 50, 10]);
    this.patch({ stayAsked: true });

    // The invitation is time-limited, so it always means "right now".
    if (this.stayTimer !== null) clearTimeout(this.stayTimer);
    this.stayTimer = window.setTimeout(() => this.patch({ stayAsked: false }), STAY_WINDOW_MS);
  }

  private onStayed(): void {
    if (this.stayTimer !== null) clearTimeout(this.stayTimer);
    play('stay');
    haptic([12, 40, 12, 40, 30]);
    this.awardStats({ stays: 1 });
    this.patch({
      stayed: true,
      stayAsked: false,
      stayInvited: false,
      notice: "You're both staying. Leaving now takes two taps.",
    });
  }

  private onSparkPresence(token: string, online: boolean, name: string): void {
    const known = this.snapshot.sparks.find((spark) => spark.token === token);
    if (!known) return;

    const sparksOnline = online
      ? [...new Set([...this.snapshot.sparksOnline, token])]
      : this.snapshot.sparksOnline.filter((item) => item !== token);

    // Only celebrate arrivals, and only when the user isn't mid-conversation.
    if (online && this.snapshot.phase === 'idle') {
      play('message');
      this.patch({ sparksOnline, notice: `${name || known.name} is online.` });
      return;
    }
    this.patch({ sparksOnline });
  }

  private onReconnectRequest(token: string, name: string): void {
    // Never interrupt an in-flight reunion with another one.
    if (this.snapshot.reconnect) return;
    play('knock');
    haptic([20, 60, 20]);
    this.patch({ reconnect: { token, name, direction: 'incoming', at: Date.now() } });

    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(
      () => this.failReconnect('timeout'),
      RECONNECT_WINDOW_MS,
    );
  }

  /* ---------------------------------------------------------------- */
  /* WebRTC                                                            */
  /* ---------------------------------------------------------------- */

  private async onMatched(message: {
    peer: PeerProfile;
    common: string[];
    polite: boolean;
    initiator: boolean;
    prompt: string | null;
    reunion: boolean;
  }): Promise<void> {
    const { peer, common, polite, initiator, prompt, reunion } = message;
    this.teardownPeer();
    this.cancelReconnect();
    this.reconnectFromIdle = false;

    this.polite = polite;
    this.makingOffer = false;
    this.ignoreOffer = false;
    this.pendingCandidates = [];

    if (reunion) this.awardStats({ reunions: 1 });

    this.patch({
      phase: 'connecting',
      peer,
      common,
      messages: [],
      unread: 0,
      liked: false,
      likedByPeer: false,
      spark: false,
      prompt,
      promptOpen: !!prompt,
      stayAsked: false,
      stayInvited: false,
      stayed: false,
      confirmNext: false,
      remoteStream: null,
      remoteHasVideo: false,
      notice: reunion ? 'Reunited ✨' : this.snapshot.notice,
    });

    const pc = new RTCPeerConnection({
      iceServers: this.iceServers as RTCIceServer[],
      iceCandidatePoolSize: 4,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    });
    this.pc = pc;

    // Both sides pre-negotiate the data channel, so neither has to wait for the
    // other's `datachannel` event before chat and reactions work.
    const channel = pc.createDataChannel('phidio', { negotiated: true, id: 0, ordered: true });
    this.bindChannel(channel);

    const remote = new MediaStream();
    pc.ontrack = (event) => {
      for (const track of event.streams[0]?.getTracks() ?? [event.track]) {
        if (!remote.getTrackById(track.id)) remote.addTrack(track);
      }
      this.patch({
        remoteStream: remote,
        remoteHasVideo: remote.getVideoTracks().some((track) => !track.muted),
      });
      event.track.onunmute = () =>
        this.patch({ remoteHasVideo: remote.getVideoTracks().length > 0 });
    };

    pc.onicecandidate = ({ candidate }) => {
      if (!candidate?.candidate) return;
      const json = candidate.toJSON();
      this.signaling.send({
        t: 'signal',
        candidate: {
          candidate: json.candidate ?? '',
          sdpMid: json.sdpMid,
          sdpMLineIndex: json.sdpMLineIndex,
          usernameFragment: json.usernameFragment,
        },
      });
    };

    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await pc.setLocalDescription();
        if (pc.localDescription) {
          this.signaling.send({
            t: 'signal',
            description: {
              type: pc.localDescription.type,
              sdp: pc.localDescription.sdp,
            },
          });
        }
      } catch {
        /* the other side will retry */
      } finally {
        this.makingOffer = false;
      }
    };

    pc.onconnectionstatechange = () => this.onConnectionStateChange(pc);
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' && !this.polite) pc.restartIce();
    };

    const stream = this.snapshot.localStream;
    if (stream) {
      for (const track of stream.getTracks()) pc.addTrack(track, stream);
    } else {
      // No camera (permission revoked mid-session): stay receive-only.
      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.addTransceiver('video', { direction: 'recvonly' });
    }

    this.tuneBitrate(pc);

    // `onnegotiationneeded` fires on both peers; the impolite side wins any
    // collision, so nudging the initiator here just shaves off a round trip.
    if (initiator && pc.signalingState === 'stable') {
      pc.dispatchEvent(new Event('negotiationneeded'));
    }
  }

  private async onRemoteSignal(message: {
    description?: { type: 'offer' | 'answer' | 'pranswer' | 'rollback'; sdp?: string };
    candidate?: RTCIceCandidateInit | null;
  }): Promise<void> {
    const pc = this.pc;
    if (!pc) return;

    try {
      if (message.description) {
        const description = message.description as RTCSessionDescriptionInit;
        const collision =
          description.type === 'offer' && (this.makingOffer || pc.signalingState !== 'stable');

        this.ignoreOffer = !this.polite && collision;
        if (this.ignoreOffer) return;

        await pc.setRemoteDescription(description);
        await this.drainCandidates();

        if (description.type === 'offer') {
          await pc.setLocalDescription();
          if (pc.localDescription) {
            this.signaling.send({
              t: 'signal',
              description: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
            });
          }
        }
      } else if (message.candidate) {
        if (!pc.remoteDescription) {
          this.pendingCandidates.push(message.candidate);
          return;
        }
        await pc.addIceCandidate(message.candidate);
      }
    } catch (error) {
      if (!this.ignoreOffer) console.warn('[phidio] signaling error', error);
    }
  }

  private async drainCandidates(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of queued) {
      await pc.addIceCandidate(candidate).catch(() => undefined);
    }
  }

  private onConnectionStateChange(pc: RTCPeerConnection): void {
    switch (pc.connectionState) {
      case 'connected': {
        if (this.snapshot.phase === 'live') return;
        play('connect');
        haptic([12, 40, 12]);
        this.patch({
          phase: 'live',
          callStartedAt: Date.now(),
          searchStartedAt: null,
          notice: null,
        });
        this.startQualityMonitor();
        break;
      }
      case 'failed':
        this.patch({ notice: 'Could not reach that person. Finding someone new…' });
        this.finishCall('disconnected');
        this.signaling.send({ t: 'skip' });
        this.beginSearch();
        break;
      case 'disconnected':
        this.patch({ quality: 'poor' });
        break;
      default:
        break;
    }
  }

  /** Caps outgoing video so mobile data plans and weak uplinks stay usable. */
  private tuneBitrate(pc: RTCPeerConnection): void {
    const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
    if (!sender) return;
    const params = sender.getParameters();
    params.encodings = params.encodings?.length ? params.encodings : [{}];
    for (const encoding of params.encodings) {
      encoding.maxBitrate = isMobile() ? 900_000 : 1_800_000;
      encoding.maxFramerate = 30;
    }
    void sender.setParameters(params).catch(() => undefined);
  }

  private startQualityMonitor(): void {
    if (this.statsTimer !== null) clearInterval(this.statsTimer);
    let lastLost = 0;
    let lastReceived = 0;

    this.statsTimer = window.setInterval(async () => {
      const pc = this.pc;
      if (!pc || pc.connectionState !== 'connected') return;

      const report = await pc.getStats().catch(() => null);
      if (!report) return;

      let lost = 0;
      let received = 0;
      let rtt = 0;

      report.forEach((entry) => {
        if (entry.type === 'inbound-rtp' && entry.kind === 'video') {
          lost += entry.packetsLost ?? 0;
          received += entry.packetsReceived ?? 0;
        }
        if (entry.type === 'candidate-pair' && entry.state === 'succeeded') {
          rtt = Math.max(rtt, (entry.currentRoundTripTime ?? 0) * 1000);
        }
      });

      const deltaLost = Math.max(0, lost - lastLost);
      const deltaReceived = Math.max(1, received - lastReceived);
      lastLost = lost;
      lastReceived = received;

      const loss = deltaLost / (deltaLost + deltaReceived);
      const quality: Quality = loss > 0.08 || rtt > 400 ? 'poor' : loss > 0.02 || rtt > 200 ? 'ok' : 'great';

      if (quality !== this.snapshot.quality) this.patch({ quality });
    }, 3000);
  }

  private teardownPeer(): void {
    if (this.statsTimer !== null) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    this.channel?.close();
    this.channel = null;

    if (this.pc) {
      this.pc.ontrack = null;
      this.pc.onicecandidate = null;
      this.pc.onnegotiationneeded = null;
      this.pc.onconnectionstatechange = null;
      this.pc.oniceconnectionstatechange = null;
      this.pc.getSenders().forEach((sender) => {
        try {
          this.pc?.removeTrack(sender);
        } catch {
          /* already closed */
        }
      });
      this.pc.close();
      this.pc = null;
    }
  }

  /* ---------------------------------------------------------------- */
  /* Data channel                                                      */
  /* ---------------------------------------------------------------- */

  private bindChannel(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let payload: DataMessage;
      try {
        payload = JSON.parse(event.data) as DataMessage;
      } catch {
        return;
      }
      switch (payload.k) {
        case 'chat':
          this.receiveChat(payload.text);
          break;
        case 'reaction':
          if (typeof payload.emoji === 'string' && payload.emoji.length <= 8) {
            haptic(6);
            this.spawnReaction(payload.emoji, 'them');
          }
          break;
        case 'typing':
          this.setPeerTyping(payload.on === true);
          break;
        default:
          break;
      }
    };
  }

  private sendData(payload: DataMessage): boolean {
    if (this.channel?.readyState !== 'open') return false;
    try {
      this.channel.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

  private receiveChat(text: string): void {
    const clean = String(text).slice(0, 500);
    if (!clean.trim()) return;
    play('message');
    this.setPeerTyping(false);
    this.pushMessage({ id: uid(), from: 'them', text: clean, at: Date.now() });
    this.patch({ unread: this.snapshot.unread + 1 });
  }

  private pushMessage(entry: ChatEntry): void {
    this.patch({ messages: [...this.snapshot.messages, entry].slice(-200) });
  }

  private setPeerTyping(on: boolean): void {
    if (this.typingTimer !== null) clearTimeout(this.typingTimer);
    this.patch({ peerTyping: on });
    if (on) {
      this.typingTimer = window.setTimeout(() => this.patch({ peerTyping: false }), 4000);
    }
  }

  private spawnReaction(emoji: string, from: 'me' | 'them'): void {
    const reaction: FloatingReaction = {
      id: uid(),
      emoji,
      from,
      x: from === 'me' ? 60 + Math.random() * 20 : 20 + Math.random() * 20,
    };
    this.patch({ reactions: [...this.snapshot.reactions, reaction].slice(-24) });

    const timer = window.setTimeout(() => {
      this.reactionTimers.delete(timer);
      this.patch({ reactions: this.snapshot.reactions.filter((item) => item.id !== reaction.id) });
    }, 1800);
    this.reactionTimers.add(timer);
  }

  /* ---------------------------------------------------------------- */
  /* Progression                                                       */
  /* ---------------------------------------------------------------- */

  private onPeerLeft(reason: SkipReason): void {
    this.finishCall(reason);
    if (this.snapshot.phase === 'idle') return;

    const messages: Partial<Record<SkipReason, string>> = {
      skipped: 'They moved on. Finding someone new…',
      left: 'They left the conversation.',
      disconnected: 'They dropped out.',
      reported: 'Conversation ended.',
      timeout: 'That conversation timed out.',
    };
    this.patch({ notice: messages[reason] ?? 'Conversation ended.' });
    this.beginSearch();
  }

  /** Records progression for a finished conversation and tears the peer down. */
  private finishCall(_reason: SkipReason): void {
    const startedAt = this.snapshot.callStartedAt;
    this.teardownPeer();

    if (this.stayTimer !== null) {
      clearTimeout(this.stayTimer);
      this.stayTimer = null;
    }
    if (this.confirmTimer !== null) {
      clearTimeout(this.confirmTimer);
      this.confirmTimer = null;
    }

    if (startedAt) {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      this.awardStats({ met: 1, seconds, longest: seconds, country: this.snapshot.peer?.country });
    }

    this.patch({
      callStartedAt: null,
      peer: null,
      common: [],
      remoteStream: null,
      remoteHasVideo: false,
      peerTyping: false,
      reactions: [],
      prompt: null,
      promptOpen: false,
      stayAsked: false,
      stayInvited: false,
      stayed: false,
      confirmNext: false,
    });
  }

  private awardStats(delta: {
    met?: number;
    sparks?: number;
    stays?: number;
    reunions?: number;
    seconds?: number;
    longest?: number;
    country?: string | null;
  }): void {
    let player: PlayerStats = { ...this.snapshot.player };

    player.met += delta.met ?? 0;
    player.sparks += delta.sparks ?? 0;
    player.stays += delta.stays ?? 0;
    player.reunions += delta.reunions ?? 0;
    player.seconds += delta.seconds ?? 0;
    if (delta.longest && delta.longest > player.longest) player.longest = delta.longest;
    if (delta.country && !player.countries.includes(delta.country)) {
      player.countries = [...player.countries, delta.country];
    }
    if (delta.met) player = bumpStreak(player);

    const earned = newlyEarnedBadges(player);
    if (earned.length) {
      player.badges = [...player.badges, ...earned.map((badge) => badge.id)];
      play('spark');
    }

    saveStats(player);
    this.patch({
      player,
      badgeToasts: earned.length ? [...this.snapshot.badgeToasts, ...earned] : this.snapshot.badgeToasts,
    });
  }
}

export const engine = new PhidioEngine();
